import React from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2, X, Globe } from 'lucide-react';
import { faviconFor, hostOf } from './businessLayout';

const STAGES = ['Reading the website', 'Understanding the business', 'Building the profile', 'Final touches'];

const stageOf = steps => {
  const last = (steps[steps.length - 1] || {}).text || '';
  if (/merging|google/i.test(last)) return 3;
  if (/synthesiz|analyzed/i.test(last)) return 2;
  if (steps.length > 2) return 1;
  return 0;
};

export const AnalysisProgress = ({ job, url, isDarkMode: d, onRetry }) => {
  const steps = job.steps || [];
  const failed = job.status === 'failed';
  const stage = stageOf(steps);
  const fav = faviconFor(url);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="analyze-progress"
      className={`rounded-3xl border p-8 md:p-10 mb-8 ${d ? 'bg-white/[0.02] border-white/[0.07]' : 'bg-white border-gray-200/80'}`}>
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${d ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'}`}>
          {fav ? <img src={fav} alt="" className="w-8 h-8 rounded-lg" /> : <Globe size={20} className="text-teal-500" />}
        </div>
        <div>
          <h3 className={`font-display text-xl font-semibold ${d ? 'text-white' : 'text-gray-900'}`}>
            {failed ? 'Analysis didn’t finish' : `Building the brand profile for ${hostOf(url) || 'this website'}`}
          </h3>
          <p className={`text-[13px] mt-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            {failed ? `${job.error || 'Something went wrong'} — you were not charged.` : 'Usually 1–2 minutes. You can leave this page — we’ll keep working and it’ll be here when you come back.'}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mt-8" data-testid="analyze-stages">
        {STAGES.map((label, i) => {
          const done = !failed && i < stage, active = !failed && i === stage;
          return (
            <div key={label} className={`rounded-xl px-4 py-3 border text-[12.5px] flex items-center gap-2 ${active ? 'border-teal-400/50 bg-teal-400/5' : d ? 'border-white/[0.07]' : 'border-gray-100'}`}>
              {done ? <Check size={14} className="text-teal-500" /> : active ? <Loader2 size={14} className="animate-spin text-teal-500" /> : <span className={`w-3.5 h-3.5 rounded-full border ${d ? 'border-white/20' : 'border-gray-300'}`} />}
              <span className={done || active ? (d ? 'text-white' : 'text-gray-900') : d ? 'text-gray-500' : 'text-gray-400'}>{label}</span>
            </div>
          );
        })}
      </div>

      <div className={`mt-6 max-h-48 overflow-y-auto space-y-2 pl-1 text-[12.5px] ${d ? 'text-gray-400' : 'text-gray-500'}`}>
        {steps.map((s, i) => (
          <motion.p key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-2">
            <Check size={12} className="text-teal-500 shrink-0" />{s.text}
          </motion.p>
        ))}
      </div>
      {failed && (
        <button type="button" onClick={onRetry} className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal-500 text-white text-xs font-semibold hover:bg-teal-600" data-testid="analyze-retry-btn">
          <X size={12} />Try again
        </button>
      )}
    </motion.div>
  );
};
